import type * as React from 'react';
import { useState } from 'react';
import { useForm } from '@inertiajs/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { toast } from 'sonner';

class SetupIntentError extends Error {}

interface PaymentMethodFormData {
  paymentMethod: {
    id: string;
    setDefault?: boolean;
  };
}

interface UseCreatePaymentMethodSubmitOptions {
  initialData: PaymentMethodFormData;
  submitPath: string;
  onSuccess?: () => void;
  setupIntentClientSecret?: string | null;
}

interface UseCreatePaymentMethodSubmitReturn {
  data: PaymentMethodFormData;
  processing: boolean;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  setPaymentMethodDefault: (setDefault: boolean) => void;
}

async function confirmSetupIntent(
  stripe: Stripe,
  elements: StripeElements,
  clientSecret: string,
): Promise<string> {
  const submitResult = await elements.submit();
  if (submitResult.error) {
    throw new Error(submitResult.error.message || 'Please review your payment details.');
  }

  const { setupIntent, error } = await stripe.confirmSetup({
    elements,
    clientSecret,
    redirect: 'if_required',
  });

  if (error || typeof setupIntent?.payment_method !== 'string') {
    throw new SetupIntentError(error?.message || 'Failed to validate payment method');
  }

  return setupIntent.payment_method;
}

export function useStripePaymentMethod() {
  const stripe = useStripe();
  const elements = useElements();

  const createPaymentMethod = async () => {
    if (!stripe || !elements) {
      throw new Error('Stripe not initialized');
    }

    const submitResult = await elements.submit();
    if (submitResult.error) {
      throw new Error(submitResult.error.message || 'Please review your payment details.');
    }

    const response = await stripe.createPaymentMethod({ elements });

    if (response.error || !response.paymentMethod?.id) {
      throw new Error(response.error?.message || 'Failed to create payment method');
    }

    return response.paymentMethod.id;
  };

  return { createPaymentMethod, stripe, elements };
}

export function useCreatePaymentMethod({
  initialData,
  submitPath,
  onSuccess,
  setupIntentClientSecret,
}: UseCreatePaymentMethodSubmitOptions): UseCreatePaymentMethodSubmitReturn {
  const { data, processing, post, setData, transform } =
    useForm<PaymentMethodFormData>(initialData);
  const { createPaymentMethod, stripe, elements } = useStripePaymentMethod();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (setupIntentClientSecret === null) {
      toast.error('Something went wrong. Please try again.');
      return;
    }

    try {
      let paymentMethodId: string;
      if (setupIntentClientSecret && stripe && elements) {
        setSubmitting(true);
        paymentMethodId = await confirmSetupIntent(stripe, elements, setupIntentClientSecret);
      } else {
        paymentMethodId = await createPaymentMethod();
      }
      transform((data) => ({
        ...data,
        paymentMethod: {
          ...data.paymentMethod,
          id: paymentMethodId,
        },
      }));

      post(submitPath, { onSuccess });
    } catch (error) {
      console.error('Payment error:', error);
      if (error instanceof SetupIntentError) {
        globalThis.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const setPaymentMethodDefault = (setDefault: boolean) => {
    setData((prev) => ({
      ...prev,
      paymentMethod: {
        ...prev.paymentMethod,
        setDefault,
      },
    }));
  };

  return {
    data,
    processing: processing || submitting,
    handleSubmit,
    setPaymentMethodDefault,
  };
}
